import React, { useState, useEffect } from 'react';
import { SpecialistIcon } from './specialistIcons';
import { getCachedUrl, setCachedUrl } from '../../utils/storageUrlCache';
import { resolveDownloadUrl } from '../../services/storageService';

/**
 * Resolve a Firebase Storage path to a download URL. Lazy-loads the storage SDK at
 * runtime (importing it at module top pulls in `undici`, which the jsdom test transform
 * can't parse). Returns null until resolved (or on error).
 *
 * The resolved URL is CACHED by path (storageUrlCache) — the immutable content we serve (avatars,
 * galleries, generated media) lives at stable paths, so a repeat mount reuses the URL instantly instead of
 * re-hitting the Storage API on every render.
 */
export function useStorageUrl(path?: string | null): string | null {
  return useStorageUrlState(path).url;
}

/**
 * The same resolution, plus whether it FAILED (BUGS.md 2026-09-27: a picture that cannot be read spun forever).
 * `url` null + `failed` false = still loading; `failed` true = the object is gone or not readable, so a caller can
 * say so in words instead of showing a spinner that never stops.
 */
export function useStorageUrlState(path?: string | null): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(() => (path ? getCachedUrl(path) : null));
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    const cached = path ? getCachedUrl(path) : null;
    setUrl(cached);
    setFailed(false);
    if (!path || cached) return; // no path, or already resolved — skip the Storage round-trip
    (async () => {
      try {
        const u = await resolveDownloadUrl(path);
        if (alive) { setCachedUrl(path, u); setUrl(u); }
      } catch {
        if (alive) setFailed(true); // object gone / no access — the caller decides how to say it
      }
    })();
    return () => { alive = false; };
  }, [path]);
  return { url, failed };
}

/** Resolve a Drive-backed avatar to an object URL via the media proxy (auth fetch → blob). */
function useDriveAvatarUrl(conversationId?: string, driveFileId?: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setUrl(null);
    if (!conversationId || !driveFileId) return;
    (async () => {
      try {
        const { fetchDriveMediaUrl } = await import('../../services/conversationMediaService');
        made = await fetchDriveMediaUrl(conversationId, driveFileId);
        if (alive) setUrl(made); else URL.revokeObjectURL(made);
      } catch { /* no access */ }
    })();
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [conversationId, driveFileId]);
  return url;
}

interface Props {
  avatarIcon?: string | null;
  avatarPath?: string | null;
  /** Drive avatar (resolved via the proxy); needs `conversationId`. */
  avatarDriveFileId?: string | null;
  /** Admin-set default avatar image (a ready URL) — shown when there is no per-chat avatar. */
  avatarImage?: string | null;
  conversationId?: string;
  name?: string;
  /** Tailwind size classes, e.g. "w-8 h-8". */
  className?: string;
  /** When set AND a real avatar image exists, the image becomes clickable and calls this with
   *  the resolved URL (e.g. to open a larger lightbox). Icon-only avatars stay non-interactive. */
  onImageClick?: (url: string) => void;
}

/** A specialist's avatar. Precedence: a per-chat user-generated image (Firebase or Drive) wins;
 *  otherwise the admin-set catalog default (`avatarImage`); otherwise the Lucide icon. */
const MemberAvatar: React.FC<Props> = ({ avatarIcon, avatarPath, avatarDriveFileId, avatarImage, conversationId, name, className = 'w-8 h-8', onImageClick }) => {
  const storageUrl = useStorageUrl(avatarDriveFileId ? null : avatarPath);
  const driveUrl = useDriveAvatarUrl(conversationId, avatarDriveFileId);
  // A resolved URL can still 404 at load time (e.g. an object not yet uploaded to this env, or an admin
  // default image path that has no file) — track the failed URL so we DEGRADE to the icon instead of
  // showing a broken-image placeholder. Keyed by URL so a later, different avatar image still gets a try.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  // Per-chat override wins; else the admin default image.
  const resolved = driveUrl || storageUrl || (!avatarPath && !avatarDriveFileId ? avatarImage || null : null);
  const url = resolved && resolved !== brokenUrl ? resolved : null;
  if (url) {
    if (onImageClick) {
      return (
        <button type="button" onClick={() => onImageClick(url)} title="View larger"
          className={`${className} rounded-full overflow-hidden cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-secondary-400`}>
          <img src={url} alt={name || 'avatar'} onError={() => setBrokenUrl(url)} className="w-full h-full object-cover" />
        </button>
      );
    }
    return <img src={url} alt={name || 'avatar'} onError={() => setBrokenUrl(url)} className={`${className} rounded-full object-cover`} />;
  }
  return (
    <span className={`${className} inline-flex items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-800`}>
      <SpecialistIcon name={avatarIcon || 'bot'} className="w-1/2 h-1/2 text-secondary-500" />
    </span>
  );
};

export default MemberAvatar;
